import { Menu } from "lucide-react";
import { Button } from "@themes/pollinator/ui/Button";
import { Sheet } from "@themes/pollinator/ui/Sheet";
import { PollinatorLogo } from "@themes/pollinator/ui/PollinatorLogo";
import { OuterContentContainer } from "@themes/pollinator/ui/OuterContentContainer";

export type Props = {
  // navigation items component
  children: React.ReactNode;
  isMobileMenuOpen: boolean;
  openMobileMenu: () => void;
  closeMobileMenu: () => void;
  /** Called when the mark is clicked, before the browser would follow it */
  onHomeClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
};

export function MarketingHeader({
  children,
  isMobileMenuOpen,
  openMobileMenu,
  closeMobileMenu,
  onHomeClick,
}: Props) {
  return (
    <OuterContentContainer
      as="header"
      // Stays at the top while the page scrolls under it, so it needs a
      // ground of its own to cover what passes beneath.
      className="sticky top-0 z-30 bg-brand-50 border-b border-brand-800 md:py-0"
      // 16px from the left edge until the desktop bar takes over.
      innerClassName="w-full justify-between max-w-[1280px] md:px-4 lg:px-8"
    >
      <div className="flex items-center justify-between pt-8 pb-[31px] w-full lg:py-4">
        {/* Left as a real link, so it can still be opened in a tab of its
            own and copied, while a plain click is handled here. */}
        <a
          href="/"
          onClick={onHomeClick}
          // A click does not take the focus, as it does not on the nav items.
          // Following the link used to carry the ring away with the page; now
          // that a click stays put, it would sit on the mark afterwards. The
          // keyboard still focuses it, with the ring.
          onMouseDown={(e: React.MouseEvent) => e.preventDefault()}
          data-testid="marketing-header-home"
          title="Home"
          aria-label="Home"
          className="with-ring focus:rounded-lg focus:ring-offset-[16px]"
        >
          <PollinatorLogo size="sm" />
        </a>

        {/* DESKTOP NAVIGATION */}
        <div className="hidden w-full items-center justify-end gap-4 lg:flex">
          {children}
        </div>

        {/* MOBILE NAVIGATION */}
        <Button
          variant="ghost"
          iconComponent={Menu}
          className="size-10 -mr-1 rounded-lg text-brand-700 [&_svg]:size-6 lg:hidden"
          onClick={openMobileMenu}
          aria-label="Open menu"
          // A screen reader is told whether the menu it opens is open.
          aria-expanded={isMobileMenuOpen}
          data-testid="marketing-header-mobile-menu-open"
        />
        {/* The panel is a dialog, and a dialog needs a name to be announced
            by; without one it was read as an unnamed dialog. */}
        <Sheet isOpen={isMobileMenuOpen} onClose={closeMobileMenu} aria-label="Menu">
          {children}
        </Sheet>
      </div>
    </OuterContentContainer>
  );
}

export default MarketingHeader;

